import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { isOnFoldersView } from './actionHelpers';

// Replica en el cliente lo que resuelve Conversations::FilterService, para
// decidir si una conversacion que llega por websocket pertenece a la lista
// filtrada (filtro aplicado o carpeta guardada) que el agente esta mirando.
//
// Devuelve tres valores: true (cumple), false (no cumple) o null (no se puede
// saber desde el payload: campos que el websocket no manda u operadores cuya
// semantica SQL no se replica). Quien llama decide que hacer con null; para
// agregar conversaciones a la lista hace falta un true explicito.

const MATCHES = true;
const DOES_NOT_MATCH = false;
const UNKNOWN = null;

const STANDARD_ATTRIBUTES = {
  status: conversation => conversation.status,
  assignee_id: conversation => conversation.meta?.assignee?.id,
  team_id: conversation => conversation.meta?.team?.id,
  inbox_id: conversation => conversation.inbox_id,
  priority: conversation => conversation.priority,
  display_id: conversation => conversation.id,
  ai_pending_approval_message_id: conversation =>
    conversation.ai_pending_approval_message_id,
  lead_temperature: conversation => conversation.lead_temperature,
};

const ADDITIONAL_ATTRIBUTES = [
  'browser_language',
  'conversation_language',
  'country_code',
  'referer',
  'mail_subject',
];

const DATE_ATTRIBUTES = ['created_at', 'last_activity_at'];

const UNSUPPORTED_STANDARD_ATTRIBUTES = [
  'bot_active',
  'campaign_id',
  // Se calcula en SQL sobre el último mensaje (Conversation::AWAITING_REPLY_SQL).
  'awaiting_reply',
];

const isBlank = value => value === undefined || value === null || value === '';

// Los ids llegan como numero en el payload y a veces como string en el filtro.
const sameValue = (a, b) => String(a) === String(b);

// Semantica SQL: `columna IN (...)` / `NOT IN (...)` con la columna en NULL no
// devuelve la fila, asi que un valor vacio no cumple ninguno de los dos.
const matchesEquality = (value, operator, values) => {
  const isIncluded = values.some(v => sameValue(v, value));
  switch (operator) {
    case 'equal_to':
      return !isBlank(value) && isIncluded;
    case 'not_equal_to':
      return !isBlank(value) && !isIncluded;
    case 'is_present':
      return !isBlank(value);
    case 'is_not_present':
      return isBlank(value);
    default:
      return UNKNOWN;
  }
};

const matchesStatus = (status, operator, values) => {
  // FilterService#filter_values: 'all' se expande a todos los estados.
  if (values.includes('all')) {
    if (operator === 'equal_to') return MATCHES;
    if (operator === 'not_equal_to') return DOES_NOT_MATCH;
  }
  return matchesEquality(status, operator, values);
};

const matchesLabels = (labels, operator, values) => {
  // FilterService#tag_filter_query: EXISTS / NOT EXISTS sobre taggings.
  const hasAnyValue = values.some(v => labels.includes(v));
  switch (operator) {
    case 'equal_to':
      return hasAnyValue;
    case 'not_equal_to':
      return !hasAnyValue;
    case 'is_present':
      return labels.length > 0;
    case 'is_not_present':
      return labels.length === 0;
    default:
      return UNKNOWN;
  }
};

const matchesText = (value, operator, values) => {
  if (operator === 'contains' || operator === 'does_not_contain') {
    if (isBlank(value)) return DOES_NOT_MATCH;
    const text = String(value).toLowerCase();
    const found = values.some(v => text.includes(String(v).toLowerCase()));
    return operator === 'contains' ? found : !found;
  }
  return matchesEquality(value, operator, values);
};

// Dia UTC (YYYY-MM-DD) o null si la fecha no es valida: toISOString tira
// RangeError con una Invalid Date y esto corre dentro del handler del websocket.
const utcDay = date =>
  Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);

const daysAgo = days => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - Number(days));
  return utcDay(date);
};

// FilterHelper#date_filter compara `(columna)::date` contra la fecha del filtro.
const matchesDate = (epochSeconds, operator, values) => {
  const date = epochSeconds
    ? utcDay(new Date(Number(epochSeconds) * 1000))
    : null;
  if (!date) return UNKNOWN;
  const limit =
    operator === 'days_before' ? daysAgo(values[0]) : String(values[0] ?? '');
  if (!limit) return UNKNOWN;
  switch (operator) {
    case 'is_greater_than':
      return date > limit;
    case 'is_less_than':
    case 'days_before':
      return date < limit;
    default:
      return UNKNOWN;
  }
};

// Atributos personalizados de conversacion (FilterService#build_custom_attr_query).
// Con valores de texto el backend compara en minusculas contra el primer valor.
// not_equal_to suma `OR ... IS NULL` con una precedencia que depende del resto
// del WHERE, asi que queda como indeterminado.
const matchesCustomAttribute = (value, operator, values) => {
  const isNull = value === undefined || value === null;
  const [first] = values;
  switch (operator) {
    case 'equal_to':
      if (isNull) return DOES_NOT_MATCH;
      if (typeof first === 'string') {
        return String(value).toLowerCase() === first.toLowerCase();
      }
      return values.some(v => sameValue(v, value));
    case 'is_present':
      return !isNull;
    case 'is_not_present':
      return isNull;
    case 'contains':
    case 'does_not_contain':
      return matchesText(value, operator, values);
    default:
      return UNKNOWN;
  }
};

const evaluateCondition = (conversation, condition) => {
  const {
    attribute_key: key,
    filter_operator: operator,
    values = [],
  } = condition;

  if (key === 'status') {
    return matchesStatus(conversation.status, operator, values);
  }
  if (key === 'labels') {
    return matchesLabels(conversation.labels || [], operator, values);
  }
  if (DATE_ATTRIBUTES.includes(key)) {
    return matchesDate(conversation[key], operator, values);
  }
  if (ADDITIONAL_ATTRIBUTES.includes(key)) {
    const additional = conversation.additional_attributes || {};
    return matchesText(additional[key], operator, values);
  }
  if (key in STANDARD_ATTRIBUTES) {
    const value = STANDARD_ATTRIBUTES[key](conversation);
    return matchesText(value, operator, values);
  }
  // bot_active, campaign_id y awaiting_reply no vienen en el payload.
  if (UNSUPPORTED_STANDARD_ATTRIBUTES.includes(key)) return UNKNOWN;
  // Cualquier otra clave el backend la resuelve como atributo personalizado
  // de conversacion (FilterHelper#handle_nil_filter).
  const customAttributes = conversation.custom_attributes || {};
  return matchesCustomAttribute(customAttributes[key], operator, values);
};

const and = (a, b) => {
  if (a === DOES_NOT_MATCH || b === DOES_NOT_MATCH) return DOES_NOT_MATCH;
  if (a === UNKNOWN || b === UNKNOWN) return UNKNOWN;
  return MATCHES;
};

const or = (a, b) => {
  if (a === MATCHES || b === MATCHES) return MATCHES;
  if (a === UNKNOWN || b === UNKNOWN) return UNKNOWN;
  return DOES_NOT_MATCH;
};

/**
 * Evalua un payload de filtros (formato de filterQueryGenerator o de
 * custom_filters.query.payload) contra una conversacion.
 *
 * El backend concatena las condiciones en un unico WHERE, por lo que AND
 * tiene precedencia sobre OR: `A and B or C` es `(A and B) or C`.
 *
 * @returns {boolean|null} true, false o null si no se puede determinar.
 */
export const evaluateConversationFilters = (conversation, payload = []) => {
  if (!payload.length) return MATCHES;

  let result = DOES_NOT_MATCH;
  let group = MATCHES;
  payload.forEach((condition, index) => {
    group = and(group, evaluateCondition(conversation, condition));
    const isLast = index === payload.length - 1;
    const isOr = String(condition.query_operator).toLowerCase() === 'or';
    if (isLast || isOr) {
      result = or(result, group);
      group = MATCHES;
    }
  });
  return result;
};

/**
 * Filtro que define la lista visible: el filtro aplicado desde el modal o, si
 * no hay, la carpeta abierta.
 *
 * @returns {Array|null} el payload, [] si la vista no tiene filtros, o null si
 *   es una carpeta cuya definicion todavia no esta disponible.
 */
export const getActiveFilterPayload = (state, rootState, rootGetters) => {
  const { appliedFilters = [] } = state;
  if (appliedFilters.length) {
    return filterQueryGenerator(appliedFilters).payload;
  }
  if (rootState?.route && isOnFoldersView(rootState)) {
    // La carpeta sale de la ruta y no de customViews/activeConversationFolder:
    // ChatList la setea desde un watcher no inmediato, asi que al volver a una
    // carpeta desde otra seccion puede quedar la anterior o null.
    const folderId = Number(rootState.route.params?.id);
    const folders = rootGetters?.['customViews/getCustomViews'] || [];
    const folder = folders.find(view => view.id === folderId);
    const payload = folder?.query?.payload;
    return Array.isArray(payload) && payload.length ? payload : null;
  }
  return [];
};

/**
 * Si una conversacion que todavia no esta en la lista puede sumarse a ella.
 * En vistas filtradas exige que cumpla el filtro con certeza; en una carpeta
 * cuya definicion aun no cargo, no deja entrar nada.
 */
export const belongsToActiveList = (
  conversation,
  { state, rootState, rootGetters }
) => {
  const payload = getActiveFilterPayload(state, rootState, rootGetters);
  if (payload === null) return false;
  if (!payload.length) return true;
  return evaluateConversationFilters(conversation, payload) === MATCHES;
};
